import { useMemo } from 'react'
import type { ItemRow } from './ItemsTable'

export type HighlightMode = 'none' | 'dupes' | 'errors' | 'attention'

interface Props {
  items: ItemRow[]
  highlightMode: HighlightMode
  onHighlight: (mode: 'dupes' | 'errors' | 'attention', uids: Set<string>) => void
  onClearHighlight: () => void
}

const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ')

// Эвристика «выглядит как партномер»: заглавные/цифры/дефис, длина ≥ 5.
// Используется, чтобы понять, не оставил ли парсер в заметке ещё один
// потенциальный партномер (типа `MS3V-T1R` в `MS3V-T1R 32.768KHZ`).
const PARTNUMBER_HINT_RE = /\b[A-Z][A-Z0-9]*[-/][A-Z0-9-/]{2,}|\b[A-Z]{2,}\d[A-Z0-9-]{2,}\b/

/** Правая панель «Результат распознавания» — сводка по позициям заявки.
 *  Три кликабельных категории-фильтра:
 *  - Дубли: позиции с одинаковым нормализованным (partNumber + brand).
 *  - С ошибками: парсер не смог разобрать поля (битые позиции с partNumber='???').
 *  - Обратить внимание: парсер вернул row с warning'ами от бэка, или в note
 *    оставил подстроку, похожую на ещё один партномер — стоит проверить.
 *  Кнопки взаимоисключающие — одна активна за раз и фильтрует обе таблицы. */
export function ParserStatsPanel({ items, highlightMode, onHighlight, onClearHighlight }: Props) {
  const stats = useMemo(() => {
    const positions = items.length

    // Дубли — по нормализованному (partNumber + brand). Пустые партномеры
    // ('???' у нераспознанных) не считаем дублями, иначе вся группа битых
    // строк подсветится сразу — бесполезно.
    const groups = new Map<string, string[]>()
    for (const row of items) {
      const pn = normalize(row.partNumber)
      const br = normalize(row.brand)
      if (!pn || pn === '???') continue
      const key = `${pn}|${br}`
      const arr = groups.get(key) ?? []
      arr.push(row.uid)
      groups.set(key, arr)
    }
    const dupUids = new Set<string>()
    let dupGroups = 0
    for (const uids of groups.values()) {
      if (uids.length >= 2) {
        dupGroups += 1
        for (const u of uids) dupUids.add(u)
      }
    }

    // С ошибками — парсер вернул row, но с предупреждениями (нет brand,
    // qty=0, parseWarnings от бэка и т.п.). Партномер при этом не пустой,
    // парсер хоть что-то понял.
    const errorUids = new Set<string>()
    // Обратить внимание — два класса позиций:
    //   1. partNumber='???' — парсер вообще не разобрал строку (раньше эти
    //      позиции отображались серыми «—» в превью). Это не «ошибка» —
    //      нечего было исправлять — просто нераспознанная строка.
    //   2. Парсер распознал чисто (needsManualReview=false), НО в note
    //      осталась подстрока, похожая на партномер. Возможно перепутал
    //      колонки или забрал partNumber не из той части строки.
    const attentionUids = new Set<string>()
    for (const row of items) {
      if (row.partNumber === '???') {
        attentionUids.add(row.uid)
        continue
      }
      if (row.needsManualReview) {
        errorUids.add(row.uid)
        continue
      }
      if (row.note && PARTNUMBER_HINT_RE.test(row.note)) {
        attentionUids.add(row.uid)
      }
    }

    // Бренды — словарь brand → количество позиций. Пустые/нераспознанные
    // в отдельную строку «без бренда».
    const brandMap = new Map<string, number>()
    let noBrand = 0
    for (const row of items) {
      const br = row.brand.trim()
      if (!br) {
        noBrand += 1
        continue
      }
      brandMap.set(br, (brandMap.get(br) ?? 0) + 1)
    }
    const brandList = Array.from(brandMap.entries()).sort((a, b) => b[1] - a[1])

    return {
      positions,
      dupUids,
      dupGroups,
      dupCount: dupUids.size,
      errorUids,
      errorCount: errorUids.size,
      attentionUids,
      attentionCount: attentionUids.size,
      brandList,
      noBrand,
    }
  }, [items])

  const toggle = (mode: 'dupes' | 'errors' | 'attention', uids: Set<string>) => {
    if (highlightMode === mode) onClearHighlight()
    else onHighlight(mode, uids)
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3 text-xs space-y-3 bg-gray-50/40 h-full min-h-0 overflow-auto">
      <div>
        <div className="text-gray-500 mb-0.5">Позиций</div>
        <div className="text-lg font-semibold text-gray-900">{stats.positions}</div>
      </div>

      <div>
        <div className="text-gray-500 mb-0.5">Дубли</div>
        {stats.dupCount === 0 ? (
          <div className="text-sm text-gray-400">нет</div>
        ) : (
          <button
            type="button"
            onClick={() => toggle('dupes', stats.dupUids)}
            className={`text-left text-sm font-medium px-2 py-1 rounded transition-colors ${
              highlightMode === 'dupes'
                ? 'bg-amber-200 text-amber-900 hover:bg-amber-300'
                : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
            }`}
            title={
              highlightMode === 'dupes'
                ? 'Снять фильтр — показать все позиции'
                : 'Показать только дубли (фильтрует обе таблицы)'
            }
          >
            {stats.dupCount} в {stats.dupGroups} {stats.dupGroups === 1 ? 'группе' : 'группах'}
            {highlightMode === 'dupes' && <span className="ml-1 text-amber-700">×</span>}
          </button>
        )}
      </div>

      <div>
        <div className="text-gray-500 mb-0.5">С ошибками</div>
        {stats.errorCount === 0 ? (
          <div className="text-sm text-gray-400">нет</div>
        ) : (
          <button
            type="button"
            onClick={() => toggle('errors', stats.errorUids)}
            className={`text-left text-sm font-medium px-2 py-1 rounded transition-colors ${
              highlightMode === 'errors'
                ? 'bg-red-200 text-red-900 hover:bg-red-300'
                : 'bg-red-100 text-red-800 hover:bg-red-200'
            }`}
            title={
              highlightMode === 'errors'
                ? 'Снять фильтр'
                : 'Показать только позиции с ошибками распознавания'
            }
          >
            {stats.errorCount} {stats.errorCount === 1 ? 'позиция' : 'позиций'}
            {highlightMode === 'errors' && <span className="ml-1 text-red-700">×</span>}
          </button>
        )}
      </div>

      <div>
        <div className="text-gray-500 mb-0.5">Обратить внимание</div>
        {stats.attentionCount === 0 ? (
          <div className="text-sm text-gray-400">нет</div>
        ) : (
          <button
            type="button"
            onClick={() => toggle('attention', stats.attentionUids)}
            className={`text-left text-sm font-medium px-2 py-1 rounded transition-colors ${
              highlightMode === 'attention'
                ? 'bg-orange-200 text-orange-900 hover:bg-orange-300'
                : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
            }`}
            title={
              highlightMode === 'attention'
                ? 'Снять фильтр'
                : 'Показать позиции, где в заметке остался похожий на партномер текст — стоит проверить'
            }
          >
            {stats.attentionCount} {stats.attentionCount === 1 ? 'позиция' : 'позиций'}
            {highlightMode === 'attention' && <span className="ml-1 text-orange-700">×</span>}
          </button>
        )}
      </div>

      <div>
        <div className="text-gray-500 mb-1">
          Брендов: <span className="font-medium text-gray-900">{stats.brandList.length}</span>
          {stats.noBrand > 0 && (
            <span className="ml-1 text-gray-400">(+ {stats.noBrand} без бренда)</span>
          )}
        </div>
        {stats.brandList.length > 0 && (
          <div className="max-h-[280px] overflow-auto space-y-0.5 pr-1">
            {stats.brandList.map(([brand, count]) => (
              <div
                key={brand}
                className="flex items-center justify-between gap-2 px-1.5 py-0.5 rounded hover:bg-white"
              >
                <span className="truncate text-gray-700" title={brand}>
                  {brand}
                </span>
                <span className="shrink-0 text-gray-500 font-mono">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
